import type { Branding } from '@logto/schemas';
import { Theme } from '@logto/schemas';
import type { Nullable } from '@silverhand/essentials';

// NOTE [ULDZA fork]: bundled brand marks shown when the tenant has not configured a
// `logoUrl`/`darkLogoUrl`. The light variant is the original ULDZA wordmark; the dark variant is
// a recolored (white) version of the same asset so it stays legible on dark surfaces.
// See `README-BRANDING.md` to swap these files.
import defaultLogo from '@/assets/brand/uldza-logo.png';
import defaultLogoDark from '@/assets/brand/uldza-logo-dark.png';

export type GetLogoUrl = {
  theme: Theme;
  logoUrl: string;
  darkLogoUrl?: Nullable<string>;
};

export const getLogoUrl = ({ theme, logoUrl, darkLogoUrl }: GetLogoUrl) => {
  if (theme === Theme.Dark) {
    return darkLogoUrl ?? logoUrl;
  }

  return logoUrl;
};

export type GetBrandingLogoUrl = {
  theme: Theme;
  branding: Branding;
  isDarkModeEnabled: boolean;
};

export const getBrandingLogoUrl = ({
  theme,
  branding,
  isDarkModeEnabled,
}: GetBrandingLogoUrl): Nullable<string> => {
  const { logoUrl, darkLogoUrl } = branding;

  // NOTE [ULDZA fork]: upstream returns `null` (no header logo) when branding is unset; we fall
  // back to the bundled ULDZA mark instead, theme-aware.
  if (!isDarkModeEnabled) {
    return logoUrl ?? defaultLogo;
  }

  if (!logoUrl && !darkLogoUrl) {
    return theme === Theme.Dark ? defaultLogoDark : defaultLogo;
  }

  if (logoUrl && darkLogoUrl) {
    return getLogoUrl({ theme, logoUrl, darkLogoUrl });
  }

  return logoUrl ?? darkLogoUrl;
};
